import { type FormEvent, useCallback, useReducer, useState } from 'react';
import { useConfig } from '../../config/ConfigContext';
import { ppAutocompleteMapper } from '../common/autocomplete/utils/autocompleteMapper';
import { storeLastSearchPrepackage } from '../common/lastSearch';
import type { AutocompleteOption, PrepackageDestination, PrepackageSearchState } from '../types';
import { popularDestinations, popularDestinationsTip, popularDestinationsTitle } from './data/popularDestinations';
import { usePrepackageInitialState } from './hooks/usePrepackageInitialState';
import { usePrepackageTotalOffers } from './hooks/usePrepackageTotalOffers';
import { usePrepackageValidation } from './hooks/usePrepackageValidation';
import { type PrepackageAutoCompleteData, PrepackageSearchForm } from './PrepackageSearchForm';
import actions, { reducer } from './prepackageActions';
import { ppDefaultState } from './prepackageDefaultState';
import { buildParams } from './utils/buildParams';
import { appendPrepackageAutocompleteParams } from './utils/geoAutocompleteParams';
import { trackPopularDestinationClick, trackPrepackageSearchClick } from './utils/tracking';

type PrePackageSearchProps = {
    prefill?: Partial<PrepackageSearchState>;
    position: string;
    geoHierarchyDestinationPickerEnabled?: boolean;
};

export const PrepackageSearch = ({ prefill, position, geoHierarchyDestinationPickerEnabled = false }: PrePackageSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, ppDefaultState);

    const ready = usePrepackageInitialState(dispatch, prefill);

    const { ppAutocompleteUrl, ppGeoHierarchyUrl, ppTotalOffersUrl, ppResultUrl } = useConfig();
    const autocompleteUrl = appendPrepackageAutocompleteParams(ppAutocompleteUrl, buildParams(state));

    const { validateState, invalid, validationErrors } = usePrepackageValidation(state, pristine);
    usePrepackageTotalOffers({ state, dispatch, totalOffersUrl: ppTotalOffersUrl });

    const handleOnSelect = useCallback(
        (option: AutocompleteOption | null, typedText?: string) => {
            if (!option && typedText) {
                dispatch(actions.setRawDestination(typedText));
            } else if (!option || option.id === 'default') {
                dispatch(actions.clearDestination());
            } else {
                if (option.id.startsWith('popular-')) {
                    trackPopularDestinationClick(option.text);
                }
                dispatch(actions.setDestination(option));
            }
        },
        [dispatch],
    );

    const handleDestinationsChange = useCallback(
        (destinations: PrepackageDestination[]) => {
            dispatch(actions.setDestinations(destinations));
        },
        [dispatch],
    );

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        setPristine(false);
        if (validateState()) {
            trackPrepackageSearchClick(position, state);
            storeLastSearchPrepackage({ ...state, totalOffers: undefined });
        } else {
            event.preventDefault();
        }
    };

    const autoCompleteData: PrepackageAutoCompleteData = {
        ppAutocompleteUrl: autocompleteUrl,
        autocompleteMapper: ppAutocompleteMapper,
        onDestinationSelect: handleOnSelect,
        initialOptions: popularDestinations,
        initialOptionsTitle: popularDestinationsTitle,
        tip: popularDestinationsTip,
    };

    return (
        <PrepackageSearchForm
            state={state}
            ready={ready}
            ppResultUrl={ppResultUrl}
            invalid={invalid}
            validationErrors={validationErrors}
            hasNoOffers={state.totalOffers === 0}
            autoCompleteData={autoCompleteData}
            destinationPickerData={{
                hierarchyUrl: ppGeoHierarchyUrl,
                selectedDestinations: state.destinations ?? [],
                onDestinationsChange: handleDestinationsChange,
            }}
            geoHierarchyDestinationPickerEnabled={geoHierarchyDestinationPickerEnabled}
            onSubmit={handleSubmit}
            onSearchTypeChange={(searchType) => dispatch(actions.setSearchType(searchType))}
            onOriginAirportChange={(originAirport) => dispatch(actions.setOriginAirport(originAirport))}
            onToggleTravelMonth={(month) => dispatch(actions.toggleTravelMonth(month))}
            onDurationChange={(duration) => dispatch(actions.setDuration(duration))}
            onDatesChange={(dates) => dispatch(actions.setDates(dates))}
            onPaxChange={(adults, childrenAges) =>
                dispatch(actions.setPaxCombination({ numberOfAdults: adults, agesOfChildren: childrenAges }))
            }
        />
    );
};
